/**
 * The Miro panel's proof of identity
 * -------------------------------------------------------------
 * `miro.board.getIdToken()` returns a JWT signed by Miro with this app's client
 * secret. Every request the panel makes carries one, and the Worker checks it.
 *
 * WHY THIS IS A MODULE OF ITS OWN, AND NOT FOUR COPIES OF THREE LINES
 *
 * It used to be three copies — in miroQueue, in workerAssetRepository, in
 * importApi — each of them:
 *
 *     const getIdToken = globalThis.miro?.board?.getIdToken
 *     if (!getIdToken) throw new Error('This is not running inside Miro')
 *     return getIdToken.call(...)
 *
 * which is correct on a page that has finished loading and wrong in the one
 * situation the panel is actually in most often: reopened.
 *
 * Miro builds a fresh iframe when the panel is opened. `miro.js` installs the
 * global synchronously, so `getIdToken` is a function immediately and the guard
 * above passes — but the postMessage channel it talks over is not up yet, and
 * until it is, the promise it returns simply never settles. It does not reject.
 * It waits.
 *
 * That is why closing and reopening the panel emptied the filter every time.
 * The tags are fetched once on mount; that fetch awaited a promise that never
 * came back; the retry around it never ran, because there was no failure to
 * retry — only silence. The queue poll survived the same problem purely by
 * accident: it runs every four seconds, so a later attempt got through once the
 * channel was up. A full board reload "fixed" it for the same reason — the
 * iframe was then built while Miro was already listening.
 *
 * So the two rules here:
 *
 *   A hang must become a failure. Nothing waits forever; a call that has not
 *   answered in a few seconds is rejected, which is something `withRetry` can
 *   actually do something about.
 *
 *   One call, shared. Six requests leave the panel at mount — tags, groups,
 *   search, sign, link, queue — and each used to ask the SDK for its own token
 *   at the same instant. They now share one, which is both less work and one
 *   less thing to be timing-dependent about.
 */

type MiroSdk = { board?: { getIdToken?: () => Promise<string> } }

/** Tokens last an hour. Replaced well before that, so none is ever used cold. */
const TOKEN_TTL_MS = 45 * 60 * 1000
/** How long to wait for the SDK global to appear at all. */
const SDK_WAIT_MS = 5000
/** How long to let one `getIdToken()` run before calling it a failure. */
const TOKEN_TIMEOUT_MS = 3000

let cached: { token: string; at: number } | null = null
let inFlight: Promise<string> | null = null

function miroSdk(): MiroSdk | undefined {
  return (globalThis as { miro?: MiroSdk }).miro
}

/** True when the Miro SDK global is present — not that it can answer yet. */
export function hasMiroSdk(): boolean {
  return typeof miroSdk()?.board?.getIdToken === 'function'
}

async function waitForSdk(): Promise<() => Promise<string>> {
  const started = Date.now()
  for (;;) {
    const board = miroSdk()?.board
    if (board && typeof board.getIdToken === 'function') {
      return () => board.getIdToken!.call(board)
    }
    if (Date.now() - started >= SDK_WAIT_MS) {
      throw new Error('This is not running inside Miro')
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
}

function withTimeout<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/**
 * A token for this panel, cached and single-flighted.
 *
 * Rejects rather than hangs, so a caller wrapped in `withRetry` gets another go
 * a moment later — which is all this problem ever needed.
 */
export async function panelToken(): Promise<string> {
  if (cached && Date.now() - cached.at < TOKEN_TTL_MS) return cached.token
  if (inFlight) return inFlight

  const attempt = (async () => {
    const getIdToken = await waitForSdk()
    const token = await withTimeout(
      getIdToken(),
      TOKEN_TIMEOUT_MS,
      'Miro did not answer in time. The panel is still starting up.',
    )
    cached = { token, at: Date.now() }
    return token
  })()

  inFlight = attempt
  try {
    return await attempt
  } finally {
    // Cleared either way: a failed attempt must not be handed to the next
    // caller, or one bad moment becomes permanent.
    if (inFlight === attempt) inFlight = null
  }
}

/**
 * Throw away the cached token.
 *
 * Called when the Worker refuses one. A token this side thinks is fine and the
 * other side rejects is the one case the clock cannot catch, and retrying with
 * the same string would fail exactly as hard.
 */
export function forgetPanelToken(): void {
  cached = null
}
